import React, { useState, useCallback, useMemo } from 'react'
import { css } from '@emotion/core'
import { DialogOverlay, Toggle, useOverlay, Button, Cell, useContainerWidth, H2, Row } from '@fs/zion-ui'
import { string, number, arrayOf, shape, oneOfType, bool } from 'prop-types'
import { useTranslation } from 'react-i18next'
import { HorizontalScroller } from '@fs/zion-ui/quarks-authorized-use-only'
import { FamilyGroupType } from '../../sharedPropTypes/familyGroupProps'
import KidsCard from '../KidsCard'
import { ColorThemeType, SplashType } from '../../sharedPropTypes/activityProps'
import { deleteActivityGroupDismiss, postActivityGroupDismiss } from '../../services'
import { getCompletedCounts } from '../../helpers/activities_helper'

const cardTogglesCss = css`
  display: flex;
  flex-direction: column;
`
const cardToggleItemCss = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
`
const hrCss = css`
  width: 100%;
  opacity: 0.2;
`

const CardToggle = ({ card, localCardToggle, handleChange, cards, index }) => (
  <>
    <div css={cardToggleItemCss}>
      <span>{card?.title}</span>
      <Toggle
        aria-label={card?.title}
        checked={!localCardToggle[card?.activityGroupId]}
        onChange={() => handleChange(card)}
      />
    </div>
    {!((cards?.length ?? 0) - 1 === index) && <hr aria-hidden css={hrCss} />}
  </>
)

export default function CardSlider({
  title = 'No Title Available',
  familyGroup,
  topicId,
  categoryId,
  cards,
  columns = 12,
  editable,
  answerCounts,
}) {
  const { t } = useTranslation()
  const atWidth = useContainerWidth()
  const renderScrollingArrows = atWidth({ default: false, lg: true })
  const editActivitiesOverlay = useOverlay()
  const [cardsToShow, setCardsToShow] = useState(cards?.filter((card) => !card?.dismissDate))
  const cardToggleSettings = useMemo(
    () =>
      cards?.reduce((obj, card) => {
        return { ...obj, [card?.activityGroupId]: !!card?.dismissDate }
      }, {}),
    [cards]
  )
  const [localCardToggle] = useState(cardToggleSettings)

  const handleChange = useCallback(
    (card) => {
      localCardToggle[card?.activityGroupId] = !localCardToggle[card?.activityGroupId]
      const filteredCards = cards?.filter(({ activityGroupId: agi }) => localCardToggle[agi] !== true)
      setCardsToShow([...filteredCards])
      if (localCardToggle[card?.activityGroupId]) {
        postActivityGroupDismiss({ activityGroupId: card?.activityGroupId })
      } else {
        deleteActivityGroupDismiss({ activityGroupId: card?.activityGroupId })
      }
    },
    [cards, localCardToggle]
  )

  return (
    <div>
      <section aria-labelledby={`cardslider-heading-${categoryId}`}>
        <Cell columns={columns}>
          <hr aria-hidden css={hrCss} />
          <Row>
            <H2 size="H5" growX alignY="middle" id={`cardslider-heading-${categoryId}`}>
              {title}
            </H2>
            {editable ? (
              <Button
                alignY="middle"
                emphasis="low"
                linkName="CardSlider:EDIT:CLICK"
                onClick={editActivitiesOverlay.open}
                aria-label={t('card.slider.showhide', 'Show/Hide Activities')}
              >
                {t('common-ui:edit.action', 'Edit')}
              </Button>
            ) : (
              <div style={{ minHeight: '40px' }} /> // makes Row height consistent when EDIT button is not rendered
            )}
          </Row>
        </Cell>
        <Cell columns={columns}>
          <HorizontalScroller
            inLayoutBand
            hideFades={!renderScrollingArrows}
            hideIcons={!renderScrollingArrows}
            buttonEmphasis="high"
            buttonColor="gray"
          >
            <ul
              style={{ display: 'flex', listStyleType: 'none', padding: 0, margin: 0 }}
              aria-labelledby={`cardslider-heading-${categoryId}`}
            >
              {cardsToShow?.map((card) => {
                return (
                  <KidsCard
                    useAsListItem
                    topicId={topicId}
                    key={card?.person?.personId ?? card?.activityGroupId ?? card?.id}
                    data={{
                      ...card,
                      count: getCompletedCounts({
                        visibleId: card?.visibleId,
                        personId: card?.person?.personId,
                        answerCounts,
                      }),
                    }}
                    familyGroup={familyGroup}
                  />
                )
              })}
            </ul>
          </HorizontalScroller>
        </Cell>
        <DialogOverlay {...editActivitiesOverlay} title={t('card.slider.showhide', 'Show/Hide Activities')}>
          <div css={cardTogglesCss}>
            <H2 size="H5">{title}</H2>
            <hr css={hrCss} />
            {cards?.map((card, index, array) => (
              <CardToggle
                key={card?.visibleId}
                index={index}
                cards={array}
                card={card}
                localCardToggle={localCardToggle}
                handleChange={handleChange}
              />
            ))}
          </div>
        </DialogOverlay>
      </section>
    </div>
  )
}

CardSlider.propTypes = {
  title: string.isRequired,
  topicId: oneOfType([string, number]),
  familyGroup: FamilyGroupType,
  cards: arrayOf(
    shape({
      activityGroupId: oneOfType([string, number]).isRequired,
      familyGroupId: oneOfType([string, number]),
      activityGroupPoints: number.isRequired,
      categoryId: oneOfType([string, number]).isRequired,
      colorTheme: ColorThemeType.isRequired,
      imageUrlSvg: string,
      splash: SplashType.isRequired,
      title: string,
    })
  ),
  columns: number,
  editable: bool,
}
